import createPDF from './createPDF';
import getCurrentVideoFrame from './getCurrentVideoFrame';
import sendNotification from '../actions/notifications/send';
import { downloadImages } from '../broadcasts/utils/frameDownloadUtils';
import { getCarouselSlideNumber } from '../components/common/modals/postCarouselSlideUtils';
declare let __PROD_API__;

const notify = (msg: string, context: 'success' | 'warning' | 'danger') =>
    sendNotification()(undefined, msg, { context });

// Video posts use the frame at the player's current position (or the first
// frame when there is no player).
export const getPostImageUrl = async (post, videoRef?) => {
    if (post.mediaType?.id !== 2) return post.cdnMediaUrl;
    return (
        await getCurrentVideoFrame({
            videoUrl: post.cdnMediaUrl,
            offset: (videoRef?.current?.currentTime ?? 0) * 1000,
        })
    ).replace('.ggops', '.rmdevops');
};

// Carousel slides get their slide number, matching the modal's "Slide N" label.
export const getPostImageFilename = post => {
    const slide = getCarouselSlideNumber(post.platformPostId);
    return `${post.socialPlatform}-${post.id}${slide ? `_slide${slide}` : ''}.jpg`;
};

const pauseVideo = videoRef => {
    try {
        videoRef?.current?.pause?.();
    } catch {
        /* ignore — pause during concurrent play() races should not break download */
    }
};

export const generatePostImage = async ({ post, videoRef, setDownloading }) => {
    if (!post?.cdnMediaUrl) {
        notify('Cannot download post that has no media available.', 'warning');
        return;
    }

    pauseVideo(videoRef);
    setDownloading(true);
    try {
        const failed = await downloadImages([
            {
                url: await getPostImageUrl(post, videoRef),
                filename: getPostImageFilename(post),
            },
        ]);
        if (failed)
            notify('Image download failed. Please try again.', 'danger');
    } catch {
        notify('Image download failed. Please try again.', 'danger');
    } finally {
        setDownloading(false);
    }
};

export const generatePostReport = async ({
    post,
    videoRef,
    username,
    content,
    annotationsWithRHLogos,
    currency,
    platform,
    primaryColor,
    secondaryColor,
    user,
    setDownloading,
}) => {
    if (!post?.cdnMediaUrl) {
        notify('Cannot download post that has no media available.', 'warning');
        return;
    }

    try {
        const isVideo = post.mediaType?.id === 2;
        pauseVideo(videoRef);
        setDownloading(true);

        const {
            height: imgHeight,
            width: imgWidth,
            postUrl,
            id: postId,
            hasPaidMetrics,
            paidEngagements,
            paidFullMediaValue,
            paidImpressions,
            paidVideoViews,
            organicEngagements,
            organicFullMediaValue,
            organicImpressions,
            organicVideoViews,
            impressions,
            videoViews,
            engagements,
            fullMediaValue,
            totalSponsorMediaValue,
        } = post;

        const env = __PROD_API__ ? 'production' : 'development';
        const { token } = user;

        const imgUrl = await getPostImageUrl(post, videoRef);

        await createPDF({
            reqBody: {
                posts: [
                    {
                        imgUrl,
                        imgHeight,
                        imgWidth,
                        username,
                        caption: content,
                        postUrl,
                        annotations: annotationsWithRHLogos,
                        impressions,
                        videoViews,
                        engagements,
                        fullMediaValue,
                        totalSponsorMediaValue,
                        currency,
                        platform,
                        showRightsholders: true,
                        postId,
                        token,
                        env,
                        hasPaidMetrics,
                        paidEngagements,
                        paidFullMediaValue,
                        paidImpressions,
                        paidVideoViews,
                        organicEngagements,
                        organicFullMediaValue,
                        organicImpressions,
                        organicVideoViews,
                        showImpressions:
                            (isVideo && impressions) ||
                            post.mediaType?.id === 1,
                        showVideoViews: isVideo && videoViews,
                    },
                ],
                options: {
                    primaryColor,
                    secondaryColor,
                },
            },
            type: 'post-pdf',
            document,
            filename: `${post.socialPlatform}-${post.id}`,
            notif: () => notify('Report PDF - Downloading', 'success'),
        });
    } catch (error) {
        // log it if needed
    } finally {
        setDownloading(false);
    }
};
